import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FaCheck, FaTimes, FaStar, FaWhatsapp, FaPhone } from 'react-icons/fa';
import PatientInfoModal from './PatientInfoModal';

const PricingPackages = ({ t }) => {
  const sectionRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('consultation');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      const elements = sectionRef.current.querySelectorAll('.fade-in, .scroll-fade-up, .scroll-fade-left, .scroll-fade-right, .scroll-zoom, .scroll-rotate');
      elements.forEach(el => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const pricingPackages = [
    {
      id: 1,
      name: "Single Session",
      subtitle: "Perfect for Assessment",
      price: "₹800",
      originalPrice: "₹1000",
      discount: "20% OFF",
      duration: "45-60 minutes", 
      category: "basic",
      popular: false,
      features: [
        "Complete physical assessment",
        "Personalized treatment plan",
        "Manual therapy session",
        "Home exercise prescription",
        "Progress evaluation"
      ],
      notIncluded: [
        "Follow-up sessions",
        "Package discounts",
        "Home visit option"
      ],
      conditions: [
        "Back Pain", "Neck Pain", "Knee Pain", "Shoulder Issues"
      ],
      bookingMessage: `🏥 *SINGLE SESSION BOOKING*
Hi Dr. Pritish! I want to book a single assessment session.

💰 Package: Single Session (₹800)
⏱️ Duration: 45-60 minutes

📋 My Details:
- Name: [Your Name]
- Age: [Age] 
- Problem: [Brief description]
- Preferred Date: [DD/MM/YYYY]
- Preferred Time: [Morning/Evening]

Please confirm availability. Thank you!`
    },
    {
      id: 2,
      name: "Weekly Package",
      subtitle: "Most Popular Choice",
      price: "₹2,800",
      originalPrice: "₹4,000", 
      discount: "30% OFF",
      duration: "5 sessions",
      category: "popular",
      popular: true,
      features: [
        "5 physiotherapy sessions",
        "Comprehensive treatment plan", 
        "Manual + exercise therapy",
        "Progress tracking",
        "Flexible scheduling",
        "WhatsApp support"
      ],
      notIncluded: [
        "Home visits",
        "Emergency consultations"
      ],
      conditions: [
        "Acute Pain", "Sports Injuries", "Post-Surgery Recovery"
      ],
      bookingMessage: `🌟 *WEEKLY PACKAGE BOOKING*
Hi Dr. Pritish! I want to book the Weekly Package.

💰 Package: Weekly Package (₹2,800 - Save ₹1,200!)
⏱️ Duration: 5 sessions

📋 My Details:
- Name: [Your Name]
- Age: [Age]
- Problem: [Detailed description]
- Preferred Days: [Mon/Wed/Fri etc.]
- Preferred Time: [Morning/Evening]

Looking forward to starting treatment!`
    },
    {
      id: 3,
      name: "Monthly Package", 
      subtitle: "Complete Recovery Plan",
      price: "₹8,500",
      originalPrice: "₹12,000",
      discount: "40% OFF",
      duration: "15 sessions",
      category: "premium",
      popular: false,
      features: [
        "15 comprehensive sessions",
        "Complete recovery program",
        "Manual + electrotherapy",
        "Exercise rehabilitation", 
        "24/7 WhatsApp support",
        "Progress reports",
        "Lifestyle counseling",
        "Maintenance plan"
      ],
      notIncluded: [
        "Home visits (add ₹200/visit)"
      ],
      conditions: [
        "Chronic Conditions", "Neurological Recovery", "Complex Injuries"
      ],
      bookingMessage: `💎 *MONTHLY PACKAGE BOOKING*
Hi Dr. Pritish! I want to book the Monthly Package for comprehensive treatment.

💰 Package: Monthly Package (₹8,500 - Save ₹3,500!)
⏱️ Duration: 15 sessions over 1 month

📋 My Details:
- Name: [Your Name]
- Age: [Age]
- Condition: [Detailed description]
- Medical History: [Any relevant info]
- Preferred Schedule: [Days & times]
- Goal: [What you want to achieve]

Ready to commit to full recovery!`
    },
    {
      id: 4,
      name: "Home Visit Package",
      subtitle: "Comfort of Your Home", 
      price: "₹1,200",
      originalPrice: "₹1,500",
      discount: "20% OFF",
      duration: "60 minutes",
      category: "premium",
      popular: false,
      features: [
        "Treatment at your home",
        "No travel required",
        "Same quality treatment",
        "Portable equipment included",
        "Family guidance included",
        "Flexible timing"
      ],
      notIncluded: [
        "Advanced electrotherapy",
        "Gym equipment exercises"
      ],
      conditions: [
        "Elderly Care", "Post-Surgery", "Mobility Issues", "Chronic Pain"
      ],
      bookingMessage: `🏠 *HOME VISIT PACKAGE BOOKING*
Hi Dr. Pritish! I need home visit physiotherapy service.

💰 Package: Home Visit Package (₹1,200 per session)
⏱️ Duration: 60 minutes at home

📋 My Details:
- Name: [Your Name]
- Age: [Age]
- Address: [Complete address with landmarks]
- Problem: [Why you need home visit]
- Preferred Date: [DD/MM/YYYY]
- Preferred Time: [Specific time]
- Contact: [Best number to reach]

Please confirm if you provide service in my area. Thank you!`
    }
  ];

  const emergencyPackage = {
    name: "Emergency Consultation",
    price: "₹1,500",
    description: "Same day urgent consultation for severe pain or injury",
    features: ["Immediate assessment", "Pain relief treatment", "Emergency care plan"],
    message: `🚨 *EMERGENCY CONSULTATION*
URGENT: I need immediate physiotherapy consultation!

💰 Package: Emergency Consultation (₹1,500)
⚡ Need: Same day treatment

🆘 Emergency Details:
- Name: [Your Name]
- Problem: [Urgent issue description]
- Pain Level (1-10): [Current pain level]
- Can Travel: [Yes/No]
- Best Contact: [Phone number]

Please respond ASAP! Thank you.`
  };

  const handleFreeConsultation = () => {
    setModalType('consultation');
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleModalSubmit = (formData) => {
    const phoneNumber = "919123860262";
    const message = encodeURIComponent(formData.message);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappURL, '_blank');
    setShowModal(false);
  };

  const handleBookPackage = (packageData) => {
    const phoneNumber = "919123860262";
    const message = encodeURIComponent(packageData.bookingMessage || packageData.message);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappURL, '_blank');
  };

  const handleCallNow = () => {
    window.location.href = "tel:+919123860262";
  };

  const getCardClass = (category, popular) => {
    let baseClass = "pricing-card h-100 hover-lift";
    if (popular) baseClass += " popular-package animate-glow";
    if (category === "premium") baseClass += " premium-package animate-shimmer";
    return baseClass;
  };

  return (
    <section id="pricing" className="section-padding" ref={sectionRef}>
      <Container>
        <Row className="justify-content-center text-center mb-5">
          <Col lg={8}>
            <h2 className="section-title scroll-fade-up title-enhance">
              Treatment Packages & Fees
            </h2>
            <p className="section-subtitle scroll-fade-up fade-in-delay-1">
              Affordable treatment options designed for your recovery journey. Choose the package that fits your needs and budget.
            </p>
          </Col>
        </Row>

        {/* Emergency Package Alert */}
        <Row className="mb-4">
          <Col lg={12}>
            <div className="emergency-package-alert scroll-zoom animate-heartbeat">
              <div className="alert-content">
                <div className="emergency-icon animate-pulse">🚨</div>
                <div className="emergency-text">
                  <strong>{emergencyPackage.name}</strong> - {emergencyPackage.description}
                  <div className="emergency-price">{emergencyPackage.price}</div>
                </div>
                <div className="emergency-actions">
                  <Button 
                    variant="danger" 
                    size="sm" 
                    className="me-2 hover-glow"
                    onClick={() => handleBookPackage(emergencyPackage)}
                  >
                    <FaWhatsapp className="me-1" /> Book Now
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    className="hover-bounce"
                    onClick={handleCallNow}
                  >
                    <FaPhone className="me-1" /> Call
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Pricing Packages */}
        <Row className="stagger-animation">
          {pricingPackages.map((pkg, index) => (
            <Col lg={3} md={6} key={pkg.id} className="mb-4">
              <Card className={getCardClass(pkg.category, pkg.popular)} style={{'--i': index}}>
                {pkg.popular && (
                  <div className="popular-badge animate-bounce-in">
                    <FaStar className="me-1" /> Most Popular
                  </div>
                )}
                
                <Card.Header className={`text-center ${pkg.popular ? 'popular-header' : ''}`}>
                  <h4 className="package-name">{pkg.name}</h4>
                  <p className="package-subtitle text-muted">{pkg.subtitle}</p>
                  
                  <div className="pricing-display">
                    {pkg.originalPrice && (
                      <div className="original-price">
                        <del>{pkg.originalPrice}</del>
                        <Badge bg="danger" className="discount-badge animate-wobble">
                          {pkg.discount}
                        </Badge>
                      </div>
                    )}
                    <div className="current-price">{pkg.price}</div>
                    <small className="duration-text">{pkg.duration}</small>
                  </div>
                </Card.Header>

                <Card.Body>
                  <div className="features-section">
                    <h6 className="features-title">✅ What's Included:</h6>
                    <ul className="features-list">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="feature-item animate-slide-up" style={{'--i': idx}}>
                          <FaCheck className="feature-check text-success me-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {pkg.notIncluded && pkg.notIncluded.length > 0 && (
                      <div className="not-included-section">
                        <h6 className="not-included-title">❌ Not Included:</h6>
                        <ul className="not-included-list">
                          {pkg.notIncluded.map((item, idx) => (
                            <li key={idx} className="not-included-item">
                              <FaTimes className="not-included-icon text-muted me-2" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="conditions-section">
                    <h6 className="conditions-title">🎯 Best For:</h6>
                    <div className="conditions-tags">
                      {pkg.conditions.map((condition, idx) => (
                        <Badge 
                          key={idx} 
                          bg="outline-primary" 
                          className="condition-tag animate-elastic"
                          style={{'--i': idx}}
                        >
                          {condition}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Card.Body>

                <Card.Footer className="text-center">
                  <Button 
                    variant={pkg.popular ? "primary" : "outline-primary"}
                    size="lg" 
                    className="w-100 mb-2 hover-glow animate-bounce-in"
                    onClick={() => handleBookPackage(pkg)}
                  >
                    <FaWhatsapp className="me-2" />
                    Book via WhatsApp
                  </Button>
                  
                  <small className="booking-note text-muted">
                    💚 Instant booking • 📞 Quick confirmation
                  </small>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Additional Benefits */}
        <Row className="mt-5">
          <Col lg={12}>
            <div className="benefits-section scroll-fade-up bg-light p-4 rounded">
              <Row className="text-center">
                <Col md={3} className="mb-3">
                  <div className="benefit-item animate-bounce-in" style={{'--i': 0}}>
                    <div className="benefit-icon">🎁</div>
                    <h6>Free Assessment</h6>
                    <small>Detailed evaluation included in all packages</small>
                  </div>
                </Col>
                <Col md={3} className="mb-3">
                  <div className="benefit-item animate-zoom-in" style={{'--i': 1}}>
                    <div className="benefit-icon">📱</div>
                    <h6>WhatsApp Support</h6>
                    <small>24/7 guidance and progress tracking</small>
                  </div>
                </Col>
                <Col md={3} className="mb-3">
                  <div className="benefit-item animate-rotate-in" style={{'--i': 2}}>
                    <div className="benefit-icon">🏆</div>
                    <h6>Success Guarantee</h6>
                    <small>Proven results or extended treatment</small>
                  </div>
                </Col>
                <Col md={3} className="mb-3">
                  <div className="benefit-item animate-flip-in" style={{'--i': 3}}>
                    <div className="benefit-icon">💳</div>
                    <h6>Flexible Payment</h6>
                    <small>Cash, UPI, or installment options</small>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Call to Action */}
        <Row className="mt-5">
          <Col lg={12} className="text-center">
            <div className="cta-section scroll-zoom animate-glow">
              <h3 className="cta-title">💫 Still Confused? Get FREE Consultation!</h3>
              <p className="cta-text">
                Not sure which package suits you? Talk to Dr. Pritish directly and get personalized recommendations.
              </p>
              <div className="cta-buttons">
                <Button 
                  variant="success" 
                  size="lg" 
                  className="me-3 hover-bounce animate-heartbeat"
                  onClick={handleFreeConsultation}
                >
                  <FaWhatsapp className="me-2" />
                  Free Consultation
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="lg"
                  className="hover-lift animate-wobble"
                  onClick={handleCallNow}
                >
                  <FaPhone className="me-2" />
                  Call Now
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Patient Info Modal */}
      <PatientInfoModal
        show={showModal}
        onHide={handleModalClose}
        onSubmit={handleModalSubmit}
        type={modalType}
        title="Free Consultation Request"
      />
    </section>
  );
};

export default PricingPackages;